import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    AccountGroup,
    DateStr,
    GUID,
    TaxableIncome,
    TaxableIncomeSaveRequest,
    TaxFilingSaveRequest,
    TaxFilingV2,
    TaxResidencySaveRequest,
    TaxResidencyV2,
} from '@ndvr/portfolio-api';

import { PortfolioApi } from 'API';

export const useTaxableIncomes = (accountGroupGUID: GUID<AccountGroup>) => {
    return useQuery({
        queryKey: ['accountGroup', accountGroupGUID, 'taxableIncome'],
        queryFn: async () => {
            const [response] = await PortfolioApi().listTaxableIncomes(accountGroupGUID);
            return response ?? undefined;
        },
    });
};

export const useTaxFilings = (accountGroupGUID: GUID<AccountGroup>) => {
    return useQuery({
        queryKey: ['accountGroup', accountGroupGUID, 'taxFiling'],
        queryFn: async () => {
            const [response] = await PortfolioApi().listTaxFilings(accountGroupGUID);
            return response ?? undefined;
        },
    });
};

export const useTaxResidencies = (accountGroupGUID: GUID<AccountGroup>) => {
    return useQuery({
        queryKey: ['accountGroup', accountGroupGUID, 'taxResidency'],
        queryFn: async () => {
            const [response] = await PortfolioApi().listTaxResidencies(accountGroupGUID);
            return response ?? undefined;
        },
    });
};

export const useCreateTaxableIncome = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: TaxableIncomeSaveRequest) => {
            const [response, error] = await PortfolioApi().createTaxableIncome(accountGroupGUID, request);
            if (error) throw error;
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'taxableIncome'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'estimatedEpwTaxRates'] });
        },
    });
};

export const useUpdateTaxableIncome = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ guid, request }: { guid: GUID<TaxableIncome>; request: TaxableIncomeSaveRequest }) => {
            const [response, error] = await PortfolioApi().updateTaxableIncome(guid, request);
            if (error) throw error;
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'taxableIncome'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'estimatedEpwTaxRates'] });
        },
    });
};

export const useDeleteTaxableIncome = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (guid: GUID<TaxableIncome>) => {
            const [response, error] = await PortfolioApi().deleteTaxableIncome(guid);
            if (error) throw error;
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'taxableIncome'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'estimatedEpwTaxRates'] });
        },
    });
};

export const useCreateTaxFiling = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: TaxFilingSaveRequest) => {
            const [response, error] = await PortfolioApi().createTaxFiling(accountGroupGUID, request);
            if (error) throw error;
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'taxFiling'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'estimatedEpwTaxRates'] });
        },
    });
};

export const useUpdateTaxFiling = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ guid, request }: { guid: GUID<TaxFilingV2>; request: TaxFilingSaveRequest }) => {
            const [response, error] = await PortfolioApi().updateTaxFiling(guid, request);
            if (error) throw error;
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'taxFiling'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'estimatedEpwTaxRates'] });
        },
    });
};

export const useDeleteTaxFiling = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (guid: GUID<TaxFilingV2>) => {
            const [response, error] = await PortfolioApi().deleteTaxFiling(guid);
            if (error) throw error;
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'taxFiling'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'estimatedEpwTaxRates'] });
        },
    });
};

export const useCreateTaxResidency = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: TaxResidencySaveRequest) => {
            const [response, error] = await PortfolioApi().createTaxResidency(accountGroupGUID, request);
            if (error) throw error;
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'taxResidency'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'estimatedEpwTaxRates'] });
        },
    });
};

export const useUpdateTaxResidency = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ guid, request }: { guid: GUID<TaxResidencyV2>; request: TaxResidencySaveRequest }) => {
            const [response, error] = await PortfolioApi().updateTaxResidency(guid, request);
            if (error) throw error;
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'taxResidency'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'estimatedEpwTaxRates'] });
        },
    });
};

export const useDeleteTaxResidency = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (guid: GUID<TaxResidencyV2>) => {
            const [response, error] = await PortfolioApi().deleteTaxResidency(guid);
            if (error) throw error;
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'taxResidency'] });
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID, 'estimatedEpwTaxRates'] });
        },
    });
};

export const useEstimatedEpwTaxRates = (
    accountGroupGUID: GUID<AccountGroup>,
    startDate?: DateStr,
    endDate?: DateStr,
) => {
    return useQuery({
        queryKey: ['accountGroup', accountGroupGUID, 'estimatedEpwTaxRates', startDate, endDate],
        queryFn: async () => {
            const [response] = await PortfolioApi().getEstimatedEpwTaxRates(accountGroupGUID, {
                startDate: startDate!,
                endDate: endDate!,
            });
            return response ?? undefined;
        },
        enabled: !!startDate && !!endDate,
    });
};
